import Scroll from "@hg/scroll"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import type React from "react"
import { useEffect, useRef, useState } from "react"

import { DialogBox } from "@/components/DialogBox"
import { ImagePreview } from "@/components/ImagePreview"
import { Modal } from "@/components/Modal"
import StrokeText from "@/components/StrokeText"
import { useTypewriter } from "@/hooks/useTypewriter"
import { i18n } from "@/i18n"
import { useEventStore, useModalStore } from "@/store"
import { getCachedDialogues, resolveDialogues } from "@/store/event"
import { collectClickShareButton } from "@/utils/event"

import styles from "./index.module.scss"

const decoPin = require("@/assets/imgs/event/deco-pin.png")

export const EventModal: React.FC = () => {
    const { currentEvent } = useEventStore()

    if (!currentEvent || currentEvent.type === "dialog") return null

    return <EventPanel />
}

const EventPanel: React.FC = () => {
    const {
        currentEvent,
        phase,
        dialogueIndex,
        selectedChoiceId,
        resolvedPostDialogues,
        selectChoice,
        closeEvent,
        setPhase,
    } = useEventStore()

    const openShare = useModalStore((state) => state.openShare)
    const shouldReduceMotion = useReducedMotion() ?? false
    const bottomRef = useRef<HTMLDivElement>(null)

    const descriptionText = currentEvent?.description
        ? i18n.get(currentEvent.description as never)
        : ""

    const selectedChoice = selectedChoiceId
        ? (currentEvent?.choices.find((c) => c.id === selectedChoiceId) ?? null)
        : null

    const choiceText = selectedChoice
        ? i18n.get(selectedChoice.text as never)
        : ""

    const resultText = selectedChoice?.resultText
        ? i18n.get(selectedChoice.resultText as never)
        : ""

    const currentPostDialogue =
        phase === "postDialog"
            ? (resolvedPostDialogues[dialogueIndex] ?? null)
            : null

    const postDialogueText = currentPostDialogue
        ? i18n.get(currentPostDialogue.text as never)
        : ""

    const { displayed, isDone, skip } = useTypewriter(
        phase === "postDialog" ? postDialogueText : "",
        40,
    )

    const {
        displayed: descDisplayed,
        isDone: descDone,
        skip: descSkip,
    } = useTypewriter(phase === "narrative" ? descriptionText : "", 40)

    const isAutoAdvance = !!(
        currentEvent?.autoAdvance && currentEvent.choices.length === 1
    )

    const {
        displayed: choiceDisplayed,
        isDone: choiceDone,
        skip: choiceSkip,
    } = useTypewriter(
        phase === "result" && !isAutoAdvance ? choiceText : "",
        40,
    )

    const [showContinueBtn, setShowContinueBtn] = useState(false)

    const [resultReady, setResultReady] = useState(false)
    const resultDelayRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
        if (phase !== "result") {
            setResultReady(false)
            return
        }
        if (isAutoAdvance || !choiceText) {
            setResultReady(true)
            return
        }
        if (choiceDone && choiceDisplayed === choiceText) {
            resultDelayRef.current = setTimeout(() => setResultReady(true), 450)
            return () => clearTimeout(resultDelayRef.current)
        }
    }, [phase, choiceDone, choiceDisplayed, choiceText, isAutoAdvance])

    const {
        displayed: resDisplayed,
        isDone: resDone,
        skip: resSkip,
    } = useTypewriter(phase === "result" && resultReady ? resultText : "", 40)

    const shownDescription =
        phase === "narrative" ? descDisplayed : descriptionText
    const shownChoice = phase === "result" ? choiceDisplayed : choiceText
    const shownResult = phase === "result" ? resDisplayed : resultText

    useEffect(() => {
        if (
            phase === "narrative" &&
            descDone &&
            descDisplayed === descriptionText &&
            descriptionText &&
            currentEvent?.choices.length
        ) {
            if (currentEvent.autoAdvance && currentEvent.choices.length === 1) {
                selectChoice(currentEvent.choices[0].id)
            } else {
                setPhase("choice")
            }
        }
    }, [
        phase,
        descDone,
        descDisplayed,
        descriptionText,
        currentEvent?.choices.length,
        currentEvent?.choices,
        currentEvent?.autoAdvance,
        selectChoice,
        setPhase,
    ])

    const dialogDelayRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
        if (
            phase === "result" &&
            resDone &&
            resDisplayed === resultText &&
            resultReady &&
            resolvedPostDialogues.length > 0
        ) {
            dialogDelayRef.current = setTimeout(
                () => setPhase("postDialog"),
                450,
            )
            return () => clearTimeout(dialogDelayRef.current)
        }
    }, [
        phase,
        resDone,
        resDisplayed,
        resultText,
        resultReady,
        resolvedPostDialogues.length,
        setPhase,
    ])

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-scroll when content changes
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "instant", block: "end" })
    }, [shownDescription, shownChoice, shownResult])

    useEffect(() => {
        if (!selectedChoiceId) {
            setShowContinueBtn(false)
            return
        }
        const allContentDone =
            resolvedPostDialogues.length > 0
                ? phase === "postDialog" &&
                  isDone &&
                  displayed === postDialogueText &&
                  postDialogueText !== "" &&
                  dialogueIndex >= resolvedPostDialogues.length - 1
                : phase === "result" &&
                  resDone &&
                  resDisplayed === resultText &&
                  resultReady
        if (allContentDone) {
            setShowContinueBtn(true)
        }
    }, [
        selectedChoiceId,
        phase,
        isDone,
        displayed,
        postDialogueText,
        dialogueIndex,
        resolvedPostDialogues.length,
        resDone,
        resDisplayed,
        resultText,
        resultReady,
    ])

    const handleContentClick = () => {
        if (phase === "narrative") {
            if (!descDone) descSkip()
        } else if (phase === "result") {
            if (!choiceDone) {
                choiceSkip()
            } else if (!resultReady) {
                clearTimeout(resultDelayRef.current)
                setResultReady(true)
            } else if (!resDone) {
                resSkip()
            } else if (resolvedPostDialogues.length > 0) {
                clearTimeout(dialogDelayRef.current)
                setPhase("postDialog")
            }
        } else if (phase === "postDialog") {
            if (!isDone) {
                skip()
            } else if (dialogueIndex < resolvedPostDialogues.length - 1) {
                useEventStore.setState({
                    dialogueIndex: dialogueIndex + 1,
                    isTyping: true,
                })
            }
        }
    }

    const handleShare = () => {
        if (!currentEvent?.image) return

        const hasAnyPostDialogues = currentEvent.choices.some(
            (c) => c.nextDialogues.length > 0,
        )

        if (!hasAnyPostDialogues) {
            openShare({ eventImage: currentEvent.image, hideDialog: true })
            return
        }

        let picked: (typeof resolvedPostDialogues)[number]
        if (phase === "postDialog" && currentPostDialogue) {
            picked = currentPostDialogue
        } else if (resolvedPostDialogues.length > 0) {
            picked =
                resolvedPostDialogues[
                    Math.floor(Math.random() * resolvedPostDialogues.length)
                ]
        } else {
            const cached = getCachedDialogues(currentEvent.id)
            if (cached) {
                picked = cached[Math.floor(Math.random() * cached.length)]
            } else {
                const allEntries = currentEvent.choices.flatMap(
                    (c) => c.nextDialogues,
                )
                const resolved = resolveDialogues(allEntries)
                picked = resolved[Math.floor(Math.random() * resolved.length)]
            }
        }

        openShare({
            eventImage: currentEvent.image,
            dialogText: i18n.get(picked.text as never),
            speaker: picked.speaker,
            expression: picked.expression,
        })
    }

    const [previewOpen, setPreviewOpen] = useState(false)

    if (!currentEvent) return null

    const postCurSide: "left" | "right" = currentPostDialogue?.side ?? "left"

    let postLeftSlot: { character: string; expression?: string } | undefined
    let postRightSlot: { character: string; expression?: string } | undefined

    if (currentPostDialogue) {
        if (postCurSide === "left") {
            postLeftSlot = {
                character: currentPostDialogue.speaker,
                expression: currentPostDialogue.expression,
            }
            for (let i = dialogueIndex - 1; i >= 0; i--) {
                if ((resolvedPostDialogues[i].side ?? "left") === "right") {
                    postRightSlot = {
                        character: resolvedPostDialogues[i].speaker,
                        expression: resolvedPostDialogues[i].expression,
                    }
                    break
                }
            }
        } else {
            postRightSlot = {
                character: currentPostDialogue.speaker,
                expression: currentPostDialogue.expression,
            }
            for (let i = dialogueIndex - 1; i >= 0; i--) {
                if ((resolvedPostDialogues[i].side ?? "left") === "left") {
                    postLeftSlot = {
                        character: resolvedPostDialogues[i].speaker,
                        expression: resolvedPostDialogues[i].expression,
                    }
                    break
                }
            }
        }
    }

    const hasImage = !!currentEvent.image

    // 处理换行符拆分成 block
    const renderLines = (text: string) =>
        text.split("\n").map((line, i) => (
            <span key={i} className={styles.textLine}>
                {line || "\u200b"}
            </span>
        ))

    const textContent = (
        <>
            {shownDescription && <p>{renderLines(shownDescription)}</p>}
            {selectedChoiceId && shownChoice && !isAutoAdvance && (
                <p className={styles.choiceLabel}>
                    {renderLines(`▸ ${shownChoice}`)}
                </p>
            )}
            {selectedChoiceId && shownResult && (
                <p>{renderLines(shownResult)}</p>
            )}
            <div ref={bottomRef} />
        </>
    )

    return (
        <>
            <Modal
                open={phase !== "idle"}
                size="event"
                showOkBtn={false}
                showCancelBtn={false}
                onCancel={closeEvent}
            >
                <div className={styles.root}>
                    <div className={styles.body} onClick={handleContentClick}>
                        {hasImage ? (
                            <>
                                <div className={styles.leftCol}>
                                    <div className={styles.cgArea}>
                                        <motion.div
                                            className={styles.cgFrame}
                                            initial={
                                                shouldReduceMotion
                                                    ? undefined
                                                    : { opacity: 0 }
                                            }
                                            animate={{ opacity: 1 }}
                                            transition={{
                                                duration: 0.5,
                                                ease: [0.22, 1, 0.36, 1],
                                                delay: 0.1,
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                setPreviewOpen(true)
                                            }}
                                        >
                                            <img
                                                className={styles.cgImage}
                                                src={currentEvent.image}
                                                alt=""
                                                draggable={false}
                                            />
                                            <motion.img
                                                className={styles.decoPin}
                                                src={decoPin}
                                                alt=""
                                                draggable={false}
                                                initial={
                                                    shouldReduceMotion
                                                        ? undefined
                                                        : {
                                                              opacity: 0,
                                                              rotate: -90,
                                                          }
                                                }
                                                animate={{
                                                    opacity: 1,
                                                    rotate: 0,
                                                }}
                                                transition={{
                                                    duration: 0.5,
                                                    ease: [0.34, 1.56, 0.64, 1],
                                                    delay: 0.35,
                                                }}
                                            />
                                        </motion.div>
                                    </div>
                                    <AnimatePresence>
                                        {phase === "postDialog" &&
                                            currentPostDialogue && (
                                                <motion.div
                                                    className={
                                                        styles.dialogWrapper
                                                    }
                                                    initial={{
                                                        opacity: 0,
                                                        ...(shouldReduceMotion
                                                            ? {}
                                                            : { y: 20 }),
                                                        scale: 0.75,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        y: 0,
                                                        scale: 0.75,
                                                    }}
                                                    exit={{
                                                        opacity: 0,
                                                        ...(shouldReduceMotion
                                                            ? {}
                                                            : { y: 20 }),
                                                        scale: 0.75,
                                                    }}
                                                    transition={{
                                                        duration: 0.3,
                                                        ease: [
                                                            0.25, 0.46, 0.45,
                                                            0.94,
                                                        ],
                                                    }}
                                                >
                                                    <DialogBox
                                                        leftCharacter={
                                                            postLeftSlot?.character
                                                        }
                                                        leftExpression={
                                                            postLeftSlot?.expression
                                                        }
                                                        rightCharacter={
                                                            postRightSlot?.character
                                                        }
                                                        rightExpression={
                                                            postRightSlot?.expression
                                                        }
                                                        active={postCurSide}
                                                        text={displayed}
                                                        fontSize={28}
                                                        lineHeight={40}
                                                    />
                                                </motion.div>
                                            )}
                                    </AnimatePresence>
                                </div>
                                <motion.div
                                    className={styles.textPanel}
                                    initial={
                                        shouldReduceMotion
                                            ? undefined
                                            : { opacity: 0 }
                                    }
                                    animate={{ opacity: 1 }}
                                    transition={{
                                        duration: 0.45,
                                        ease: [0.25, 0.46, 0.45, 0.94],
                                        delay: 0.2,
                                    }}
                                >
                                    <Scroll
                                        direction="y"
                                        scrollBar
                                        autoHideScrollBar={false}
                                        className={styles.textScroll}
                                        scrollBarClassName={styles.scrollBar}
                                        thumbClassName={styles.scrollThumb}
                                        scrollByDrag
                                        rotateDetection
                                    >
                                        <div
                                            className={styles.textScrollContent}
                                        >
                                            {textContent}
                                        </div>
                                    </Scroll>
                                </motion.div>
                            </>
                        ) : (
                            <motion.div
                                className={styles.contentPanel}
                                initial={
                                    shouldReduceMotion
                                        ? undefined
                                        : { opacity: 0, y: -18 }
                                }
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.3,
                                    ease: [0.25, 0.46, 0.45, 0.94],
                                    delay: 0.1,
                                }}
                            >
                                <Scroll
                                    direction="y"
                                    scrollBar
                                    autoHideScrollBar={false}
                                    maskSize="0px"
                                    className={styles.noCgText}
                                    scrollBarClassName={styles.noCgScrollBar}
                                    thumbClassName={styles.scrollThumb}
                                    scrollByDrag
                                >
                                    <div className={styles.noCgTextContent}>
                                        {textContent}
                                    </div>
                                </Scroll>
                                <AnimatePresence>
                                    {phase === "postDialog" &&
                                        currentPostDialogue && (
                                            <motion.div
                                                className={styles.noCgDialog}
                                                initial={{
                                                    opacity: 0,
                                                    ...(shouldReduceMotion
                                                        ? {}
                                                        : { y: 20 }),
                                                    scale: 0.75,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    y: 0,
                                                    scale: 0.75,
                                                }}
                                                exit={{
                                                    opacity: 0,
                                                    ...(shouldReduceMotion
                                                        ? {}
                                                        : { y: 20 }),
                                                    scale: 0.75,
                                                }}
                                                transition={{
                                                    duration: 0.3,
                                                    ease: [
                                                        0.25, 0.46, 0.45, 0.94,
                                                    ],
                                                }}
                                            >
                                                <DialogBox
                                                    leftCharacter={
                                                        postLeftSlot?.character
                                                    }
                                                    leftExpression={
                                                        postLeftSlot?.expression
                                                    }
                                                    rightCharacter={
                                                        postRightSlot?.character
                                                    }
                                                    rightExpression={
                                                        postRightSlot?.expression
                                                    }
                                                    active={postCurSide}
                                                    text={displayed}
                                                    fontSize={28}
                                                    lineHeight={40}
                                                />
                                            </motion.div>
                                        )}
                                </AnimatePresence>
                            </motion.div>
                        )}
                    </div>

                    <div className={styles.footer}>
                        <AnimatePresence mode="wait">
                            {phase === "choice" &&
                                currentEvent.choices.length > 0 && (
                                    <motion.div
                                        key="choices"
                                        className={styles.choicesWrapper}
                                        initial={
                                            shouldReduceMotion
                                                ? undefined
                                                : "hidden"
                                        }
                                        animate="visible"
                                        exit={{
                                            opacity: 0,
                                            y: 5,
                                            transition: {
                                                duration: 0.25,
                                                ease: [0.25, 0.46, 0.45, 0.94],
                                            },
                                        }}
                                        variants={{
                                            hidden: {},
                                            visible: {
                                                transition: {
                                                    staggerChildren:
                                                        shouldReduceMotion
                                                            ? 0
                                                            : 0.08,
                                                },
                                            },
                                        }}
                                    >
                                        {currentEvent.choices.map((choice) => (
                                            <motion.button
                                                key={choice.id}
                                                className={styles.choiceBtn}
                                                type="button"
                                                disabled={phase !== "choice"}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    selectChoice(choice.id)
                                                }}
                                                variants={{
                                                    hidden: {
                                                        opacity: 0,
                                                        y: 5,
                                                    },
                                                    visible: {
                                                        opacity: 1,
                                                        y: 0,
                                                        transition: {
                                                            duration: 0.3,
                                                            ease: [
                                                                0.25, 0.46,
                                                                0.45, 0.94,
                                                            ],
                                                        },
                                                    },
                                                }}
                                                whileHover={
                                                    shouldReduceMotion
                                                        ? undefined
                                                        : {
                                                              y: -1,
                                                              scale: 1.02,
                                                          }
                                                }
                                                whileTap={
                                                    shouldReduceMotion
                                                        ? undefined
                                                        : {
                                                              y: 1,
                                                              scale: 0.96,
                                                          }
                                                }
                                                transition={{
                                                    duration: 0.15,
                                                    ease: "easeOut",
                                                }}
                                            >
                                                <StrokeText
                                                    className={
                                                        styles.choiceText
                                                    }
                                                    text={i18n.get(
                                                        choice.text as never,
                                                    )}
                                                    fontFamily="source-han-sans-japanese"
                                                    fontWeight={700}
                                                    fontSize={30}
                                                    fillColor="#fff"
                                                    borderWidth={4}
                                                    borderColor="#564426"
                                                    offsetY={0}
                                                    padding={4}
                                                    maxWidth={340}
                                                    maxLines={2}
                                                    minFontSize={22}
                                                    baselineAligned
                                                />
                                            </motion.button>
                                        ))}
                                    </motion.div>
                                )}

                            {showContinueBtn && (
                                <motion.div
                                    key="continue"
                                    className={styles.choicesWrapper}
                                    initial={
                                        shouldReduceMotion
                                            ? undefined
                                            : "hidden"
                                    }
                                    animate="visible"
                                    exit="hidden"
                                    variants={{
                                        hidden: {},
                                        visible: {
                                            transition: {
                                                staggerChildren:
                                                    shouldReduceMotion
                                                        ? 0
                                                        : 0.08,
                                            },
                                        },
                                    }}
                                >
                                    <motion.button
                                        className={styles.choiceBtn}
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            closeEvent()
                                        }}
                                        variants={{
                                            hidden: {
                                                opacity: 0,
                                                y: 5,
                                            },
                                            visible: {
                                                opacity: 1,
                                                y: 0,
                                                transition: {
                                                    duration: 0.3,
                                                    ease: [
                                                        0.25, 0.46, 0.45, 0.94,
                                                    ],
                                                },
                                            },
                                        }}
                                        whileHover={
                                            shouldReduceMotion
                                                ? undefined
                                                : {
                                                      y: -1,
                                                      scale: 1.02,
                                                  }
                                        }
                                        whileTap={
                                            shouldReduceMotion
                                                ? undefined
                                                : {
                                                      y: 1,
                                                      scale: 0.96,
                                                  }
                                        }
                                        transition={{
                                            duration: 0.15,
                                            ease: "easeOut",
                                        }}
                                    >
                                        <StrokeText
                                            className={styles.choiceText}
                                            text={i18n.get(
                                                "map.event.continueForward" as never,
                                            )}
                                            fontFamily="source-han-sans-japanese"
                                            fontWeight={700}
                                            fontSize={30}
                                            fillColor="#fff"
                                            borderWidth={4}
                                            borderColor="#564426"
                                            offsetY={0}
                                            padding={4}
                                            maxWidth={340}
                                            baselineAligned
                                        />
                                    </motion.button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        {hasImage && (
                            <motion.button
                                className={styles.shareBtn}
                                type="button"
                                initial={
                                    shouldReduceMotion
                                        ? undefined
                                        : { opacity: 0, x: 20 }
                                }
                                animate={{ opacity: 1, x: 0 }}
                                whileHover={
                                    shouldReduceMotion
                                        ? undefined
                                        : {
                                              y: -1,
                                              scale: 1.02,
                                          }
                                }
                                whileTap={
                                    shouldReduceMotion
                                        ? undefined
                                        : {
                                              y: 1,
                                              scale: 0.96,
                                          }
                                }
                                transition={{
                                    duration: 0.15,
                                    ease: "easeOut",
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    collectClickShareButton(
                                        "game",
                                        currentEvent.id,
                                    )
                                    handleShare()
                                }}
                            >
                                <StrokeText
                                    className={styles.btnText}
                                    text={i18n.get(
                                        "map.gallery.share" as never,
                                    )}
                                    fontFamily="source-han-sans-japanese"
                                    fontWeight={700}
                                    fontSize={36}
                                    fillColor="#fff"
                                    borderWidth={4}
                                    borderColor="#564426"
                                    offsetY={0}
                                    padding={4}
                                    baselineAligned
                                />
                            </motion.button>
                        )}
                    </div>
                </div>
            </Modal>
            <ImagePreview
                open={previewOpen}
                src={currentEvent.image}
                onClose={() => setPreviewOpen(false)}
            />
        </>
    )
}
